import { Pagination, Select, Table } from 'antd';
import PropTypes from 'prop-types';
import { useRef, useEffect } from 'react';

const { Option } = Select;

export const TableCustom = ({
  rowKey = "id",
  columns,
  dataSource,
  totalItem,
  params,
  setParams,
  loading,
  rowSelection,
  scroll,
  scrollY,
}) => {
  const wrapperRef = useRef(null);
  const topScrollRef = useRef(null);
  const syncing = useRef(false);

  // Dùng MutationObserver để tìm .ant-table-body khi Ant Design mount xong
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    let attached = null;
    let roCleanup = () => {};
    let rafId = null;

    const attach = (body) => {
      if (attached === body) return; // already attached
      if (attached) {
        attached.removeEventListener('scroll', onBodyScroll);
        roCleanup();
      }
      attached = body;

      // Sync width
      const syncWidth = () => {
        const top = topScrollRef.current;
        if (!top || !body) return;
        const inner = top.firstChild;
        if (inner) inner.style.width = `${body.scrollWidth}px`;
      };
      syncWidth();
      const ro = new ResizeObserver(syncWidth);
      ro.observe(body);
      roCleanup = () => ro.disconnect();

      body.addEventListener('scroll', onBodyScroll);
    };

    const onBodyScroll = (e) => {
      if (syncing.current) return;
      const top = topScrollRef.current;
      if (top) { syncing.current = true; top.scrollLeft = e.target.scrollLeft; syncing.current = false; }
    };

    // MutationObserver: watch for .ant-table-body appearing or re-rendering
    // Dùng rAF debounce để tránh gọi quá nhiều lần khi checkbox thay đổi DOM
    const mo = new MutationObserver(() => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const body = wrapper.querySelector('.ant-table-body');
        if (body) attach(body);
      });
    });
    mo.observe(wrapper, { childList: true, subtree: true });

    // Try immediately in case already mounted
    const body = wrapper.querySelector('.ant-table-body');
    if (body) attach(body);

    return () => {
      mo.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
      if (attached) attached.removeEventListener('scroll', onBodyScroll);
      roCleanup();
    };
  }, []); // Only once

  const handleTopScroll = (e) => {
    if (syncing.current) return;
    const body = wrapperRef.current?.querySelector('.ant-table-body');
    if (body) { syncing.current = true; body.scrollLeft = e.target.scrollLeft; syncing.current = false; }
  };

  return (
    <div ref={wrapperRef}>
      {/* Thanh cuộn ngang ngay trên body bảng */}
      <div
        ref={topScrollRef}
        onScroll={handleTopScroll}
        style={{ overflowX: 'auto', overflowY: 'hidden', height: 14 }}
      >
        <div style={{ height: 1 }} />
      </div>

      <Table
        rowKey={rowKey}
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        pagination={false}
        scroll={{ ...(scroll || { x: 750 }), y: scrollY ?? 500 }}
        tableLayout="fixed"
        rowSelection={rowSelection}
        sticky={{ offsetHeader: 0 }}
        footer={
          totalItem
            ? () => (
                <div className="flex flex-row justify-between">
                  <Pagination
                    pageSize={params?.limit}
                    showSizeChanger={false}
                    defaultCurrent={params?.page}
                    total={totalItem}
                    onChange={(page) => setParams({ ...params, page: page })}
                  />
                  <div className="flex flex-row justify-end items-center gap-2">
                    <Select
                      showSearch
                      value={params?.limit}
                      onChange={(value) => setParams({ ...params, page: 1, limit: value })}
                    >
                      <Option value={10}>10</Option>
                      <Option value={20}>20</Option>
                      <Option value={50}>50</Option>
                      <Option value={100}>100</Option>
                    </Select>
                    <div className="text-sm text-[#667085] font-medium">
                      Hiển thị từ {totalItem ? (params?.page - 1) * params?.limit + 1 : 0} -{' '}
                      {params?.page * params?.limit > totalItem ? totalItem : params?.page * params?.limit} của{' '}
                      {totalItem}
                    </div>
                  </div>
                </div>
              )
            : null
        }
        bordered
        rowClassName={(record, index) =>
          index % 2 === 0 ? 'bg-gray-100 hover:bg-gray-200' : 'bg-white hover:bg-gray-200'
        }
        className="bg-white border rounded-lg"
      />
    </div>
  );
};

TableCustom.propTypes = {
  columns: PropTypes.array.isRequired,
  dataSource: PropTypes.array,
};
